import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useSeo } from "@/hooks/useSeo";
import { MEDIA } from "@/lib/media";

export const CONTENT = {
  about: {
    hero: MEDIA.hero, overline: "Who We Are", title: "About Golden Wolf",
    lead: "Golden Wolf Foundation is a Chicago nonprofit dedicated to empowering veterans to achieve their full potential — pairing them with service dogs, training the next generation of canine professionals, and building a self-sustaining campus in North Lawndale.",
    sections: [
      { h: "Service Dogs Save Lives", p: "Every year, more than 6,000 veterans lose their battle with suicide. Our goal is to provide 6,000 service dogs to our veterans every year — trained partners that restore independence, safety and purpose." },
      { h: "The Golden Wolf Foundation Academy", p: "GWFA is a transformative, veteran-led development activating over 33,000 square feet of land at 3658–3636 W. 16th Street in Chicago's North Lawndale community — a $25 million catalytic project designed as a holistic ecosystem of human service, social enterprise and community space." },
      { h: "A self-sustaining model", p: "Revenue-generating operations — a premium 'Golden Wolf' pet food production center, a public food hall, and a shared commercial kitchen — directly subsidize workforce development for veterans and returning citizens, creating a scalable, self-reinforcing impact model." },
      { h: "National vision", p: "We're building state-of-the-art facilities in Chicago and Milwaukee, with the ultimate goal of a Golden Wolf training facility in every city in the United States by 2030." },
    ],
    ctas: [["Meet the Golden Founder", "/golden-founder"], ["Our mission", "/mission"]],
  },
  mission: {
    hero: MEDIA.veteranDog, overline: "Our Mission", title: "Integrated Impact",
    lead: "Golden Wolf Foundation Academy transforms lives and communities by providing veterans and returning citizens with accredited pathways into high-growth careers — through best-in-class canine training, ethical food production, and cooperative entrepreneurship, anchored in North Lawndale.",
    sections: [
      { h: "Talent development", p: "Become the Midwest's premier accredited pipeline for certified canine trainers, behaviorists and handlers — focusing on veterans and returning citizens. We also train Junior Dog Trainers (grades 8–12) to assist in service-dog training." },
      { h: "Economic engine", p: "A circular economy where social-enterprise profits fund training, and graduate success fuels local business growth." },
      { h: "Community hub", p: "Activate a blighted corridor with vibrant public amenities — a dog park and food court — plus incubator space for local food entrepreneurs." },
      { h: "Industry leadership", p: "Elevate standards in both the canine services and pet food industries through veteran discipline, ethical practices and scientific rigor." },
    ],
    ctas: [["Donate now", "/donate"], ["Apply for a service dog", "/veterans/apply"]],
  },
  "service-dog-program": {
    hero: MEDIA.veteranDog, overline: "Program", title: "Service Dog Program",
    lead: "A privacy-first path from initial inquiry to lifelong partnership, matching veterans with professionally trained working dogs.",
    sections: [
      { h: "How it works", p: "We start with a confidential screening (never a public diagnosis), match you with a dog and trainer, and support the full journey — training, placement and follow-up. Applying does not guarantee acceptance or placement." },
      { h: "Standards of care", p: "Every dog is trained humanely with veterinary oversight, rest protocols and public-access preparation." },
    ],
    ctas: [["Start your application", "/veterans/apply"], ["Talk to us", "/contact"]],
  },
  "trainer-program": {
    hero: MEDIA.trainer, overline: "Program", title: "Master Dog Trainer Program",
    lead: "Become a certified professional dog trainer through a structured apprenticeship — with dedicated tracks for veterans and civilians.",
    sections: [
      { h: "Five tracks", p: "Veteran Apprentice, Civilian Apprentice, Experienced Trainer, Volunteer Puppy Raiser and Veteran Peer Mentor — there's a path for every level of experience." },
      { h: "What you gain", p: "Hands-on training, mentorship, background-checked professional credentials, and the chance to serve veterans through the dogs you raise." },
    ],
    ctas: [["Become a trainer", "/trainer/apply"], ["Volunteer instead", "/volunteer"]],
  },
  veterans: {
    hero: MEDIA.veteranDog, overline: "For Veterans", title: "You've Served. Now Let Us Serve You.",
    lead: "Whether you need a service dog, want to become a trainer, or just want to be part of the pack again — there's a place for you here.",
    sections: [
      { h: "Your privacy is protected", p: "We never publish your disability or medical information. Sensitive documents are only requested securely at a later stage and are restricted to authorized staff." },
      { h: "More than a dog", p: "Placement is the beginning. Peer mentorship, community events and the campus give you a mission for the long haul." },
    ],
    ctas: [["Apply for a service dog", "/veterans/apply"], ["Become a trainer", "/trainer/apply"]],
  },
  "financial-transparency": {
    overline: "Trust", title: "Financial Transparency",
    lead: "We believe donors deserve to see exactly how their generosity becomes impact.",
    sections: [
      { h: "Where your money goes", p: "Donations are designated to Service Dog Training, Veteran Trainer Scholarships, Veterinary Care, the Golden Wolf Campus, the 14DDV Event, or Greatest Need — and we report on each." },
      { h: "Our commitment", p: "We do not promise tax deductibility beyond what is legally permitted, and we provide acknowledgment receipts for every gift. Annual reports and financial summaries are made available to our community." },
    ],
    ctas: [["Donate", "/donate"], ["Contact us", "/contact"]],
  },
  privacy: {
    overline: "Legal", title: "Privacy Policy",
    lead: "Golden Wolf Foundation is committed to protecting the privacy and dignity of veterans, donors and volunteers.",
    sections: [
      { h: "Information we collect", p: "We collect only the information necessary to process donations, applications and registrations. Veteran medical and disability information is never published and is stored with restricted access." },
      { h: "How we protect it", p: "Data is transmitted over encrypted connections. Uploaded verification documents are stored privately and are not exposed in public storage. We apply rate limiting and access controls throughout." },
      { h: "Your choices", p: "You control public recognition and anonymity for donations, and you may request access to or deletion of your personal data by contacting us." },
    ],
    ctas: [["Contact us", "/contact"]],
  },
  terms: {
    overline: "Legal", title: "Terms of Use",
    lead: "By using GoldenWolfVets.org you agree to the following terms.",
    sections: [
      { h: "Donations", p: "All donations are voluntary. Submitting a donation or pledge does not create any obligation on the part of Golden Wolf Foundation. Tax deductibility is limited to what is legally permitted." },
      { h: "Applications", p: "Submitting a veteran or trainer application does not guarantee acceptance or placement. Program participation is subject to screening and available capacity." },
      { h: "Conduct & animal welfare", p: "Participants agree to follow all safety and humane animal-welfare guidelines at events and on campus." },
    ],
    ctas: [["Contact us", "/contact"]],
  },
  accessibility: {
    overline: "Commitment", title: "Accessibility",
    lead: "We are committed to meeting WCAG 2.2 AA standards so every veteran and visitor can use this site.",
    sections: [
      { h: "What we do", p: "High text contrast, descriptive image alt text, full keyboard navigation, respect for reduced-motion preferences, and optimized, fast-loading pages on mobile." },
      { h: "Tell us", p: "If you encounter any barrier using this website, please contact us so we can fix it quickly." },
    ],
    ctas: [["Contact us", "/contact"]],
  },
};

export default function ContentPage({ slug }) {
  const c = CONTENT[slug];
  useSeo(c?.title, c?.lead);
  if (!c) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-[#C8C4B7]">Page not found.</div>;
  return (
    <div>
      {c.hero && (
        <section className="relative h-[45vh] flex items-end grain" data-testid={`content-hero-${slug}`}>
          <img src={c.hero} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
          <div className="relative max-w-4xl mx-auto px-6 pb-12 w-full">
            <div className="overline mb-3">{c.overline}</div>
            <h1 className="font-heading text-4xl sm:text-6xl font-bold text-[#FDFBF7]">{c.title}</h1>
          </div>
        </section>
      )}
      <div className="max-w-3xl mx-auto px-6 py-16">
        {!c.hero && (<><div className="overline mb-3">{c.overline}</div><h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#FDFBF7]">{c.title}</h1></>)}
        <p className="text-xl text-[#C8C4B7] leading-relaxed mt-6">{c.lead}</p>
        <div className="mt-12 space-y-10">
          {c.sections.map((s, i) => (
            <motion.div key={s.h} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <h2 className="font-heading text-2xl text-[#D4AF37]">{s.h}</h2>
              <p className="mt-3 text-[#C8C4B7] leading-relaxed text-lg">{s.p}</p>
            </motion.div>
          ))}
        </div>
        {c.ctas && (
          <div className="mt-12 flex gap-3 flex-wrap">
            {c.ctas.map(([label, to], i) => (
              <Link key={to} to={to} data-testid={`content-cta-${i}`} className={i === 0 ? "btn-gold" : "btn-outline"}>{label}</Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
